///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Box3, Object3D } from "three";

import { ModelImpl } from "../../models/ModelImpl";
import { DynamicGltfLoader } from "./DynamicGltfLoader.js";

// Dynamic model implementation.

export class DynamicModelImpl extends ModelImpl {
  public gltfLoader: DynamicGltfLoader;
  public modelId: number;

  override getExtents(target: Box3): Box3 {
    return target.union(this.gltfLoader.getTotalGeometryExtent());
  }

  override getObjects(): Object3D[] {
    const objects = [];
    this.gltfLoader.originalObjects.forEach((object: Object3D) => {
      objects.push(object);
    });
    return objects;
  }

  override getVisibleObjects(): Object3D[] {
    return this.gltfLoader.getOriginalObjectForSelect();
  }

  override hasObject(object: any): boolean {
    return this.gltfLoader.originalObjects.has(object);
  }

  override getObjectsByHandles(handles: string | string[]): Object3D[] {
    const handlesSet = new Set(handles);
    const objects = [];
    handlesSet.forEach((handle) => {
      const handle2 = `${this.modelId}_${handle}`; // <- props fix: Dynamic Loader uses handle with structure ID prefix
      const handles = this.gltfLoader.handleToObjects.get(handle2) || [];
      objects.push(...Array.from(handles));
    });
    return objects;
  }

  override getHandlesByObjects(objects: Object3D | Object3D[]): string[] {
    const handles = super.getHandlesByObjects(objects);
    return handles.map((x) => x.split("_").pop()); // <- props fix: remove structure ID prefix by the Dynamic Loader
  }

  override hideObjects(objects: Object3D | Object3D[]): this {
    const handles = super.getHandlesByObjects(objects);
    this.gltfLoader.hideObjects(handles);
    return this;
  }

  override isolateObjects(objects: Object3D | Object3D[]): this {
    const handles = super.getHandlesByObjects(objects);
    this.gltfLoader.isolateObjects(new Set(handles));
    return this;
  }

  override showObjects(objects: Object3D | Object3D[]): this {
    const handles = super.getHandlesByObjects(objects);
    this.gltfLoader.showObjects(handles);
    return this;
  }

  override showAllObjects(): this {
    this.gltfLoader.showAllHiddenObjects();
    return this;
  }

  override showOriginalObjects(objects: Object3D | Object3D[]): this {
    this.gltfLoader.showOriginalObjects(objects);
    return this;
  }

  override hideOriginalObjects(objects: Object3D | Object3D[]): this {
    this.gltfLoader.hideOriginalObjects(objects);
    return this;
  }
}
